import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Facebook, Instagram, Mail, Phone, MapPin, Star } from 'lucide-react';
import { useSettings, resolveImageUrl } from '../context/SettingsContext';
import { useGoogleRating } from '../hooks/useGoogleRating';

const Footer = () => {
  const { settings } = useSettings();
  const contact = settings?.contact || {};
  const footer = settings?.footer || {};
  const logoUrl = settings?.brand?.logo_url;
  const rating = useGoogleRating();
  const [cities, setCities] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/standorte`)
      .then((r) => setCities(r.data?.items || []))
      .catch(() => {});
  }, []);
  const ratingValue = rating.rating != null
    ? rating.rating.toFixed(1).replace('.', ',')
    : null;
  const ratingTotal = rating.total;

  return (
    <footer className="text-sky-50" style={{ backgroundColor: '#005898' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="space-y-4">
            {logoUrl && (
              <img src={resolveImageUrl(logoUrl)} alt="Logo" className="h-20 md:h-24 w-auto" />
            )}
            {ratingValue && ratingTotal && (
              <a
                href={rating.url || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-full pl-2 pr-3.5 py-1.5 group"
                data-testid="footer-google-rating"
              >
                <span className="flex items-center bg-white rounded-full px-1.5 py-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                  ))}
                </span>
                <span className="text-sm text-sky-50 font-medium">
                  <span className="font-bold text-white">{ratingValue}</span> · {ratingTotal} Google-Bewertungen
                </span>
              </a>
            )}
            <p className="text-sm leading-relaxed text-sky-100/90">
              {footer.description || 'Ihr Experte für Hüpfburgen und Eventverleih.'}
            </p>
          </div>

          <div>
            <h3 className="font-bold text-yellow-300 mb-4">{footer.navigation_title || 'Navigation'}</h3>
            <ul className="space-y-2 text-sm">
              {(Array.isArray(footer.navigation_links) && footer.navigation_links.length > 0
                ? footer.navigation_links
                : [
                    { label: 'Home', url: '/' },
                    { label: 'Produkte', url: '/produkte' },
                    { label: 'Service', url: '/service' },
                    { label: 'Über Uns', url: '/ueber-uns' },
                    { label: 'Ratgeber', url: '/blog' },
                  ]
              ).filter((l) => l && l.label && l.url).map((l, idx) => {
                const isExternal = /^https?:/i.test(l.url);
                const target = isExternal ? '_blank' : undefined;
                const rel = isExternal ? 'noopener noreferrer' : undefined;
                return (
                  <li key={`${l.url}-${idx}`}>
                    {isExternal ? (
                      <a href={l.url} target={target} rel={rel} className="hover:text-yellow-300 transition-colors">
                        {l.label}
                      </a>
                    ) : (
                      <Link to={l.url} className="hover:text-yellow-300 transition-colors" data-testid={`footer-link-${idx}`}>
                        {l.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-yellow-300 mb-4">Kontakt</h3>
            <ul className="space-y-3 text-sm">
              {contact.address && (
                <li className="flex items-start space-x-2">
                  <MapPin className="h-5 w-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                  <span>{contact.address}</span>
                </li>
              )}
              {contact.phone && (
                <li className="flex items-center space-x-2">
                  <Phone className="h-5 w-5 text-yellow-300" />
                  <span>{contact.phone}</span>
                </li>
              )}
              {contact.email && (
                <li className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-yellow-300" />
                  <span>{contact.email}</span>
                </li>
              )}
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-yellow-300 mb-4">Folge uns</h3>
            <div className="flex space-x-3">
              {contact.facebook_url && (
                <a
                  href={contact.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 text-white p-3 rounded-full hover:bg-yellow-400 hover:text-sky-900 transition-colors"
                  data-testid="facebook-link"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {contact.instagram_url && (
                <a
                  href={contact.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 text-white p-3 rounded-full hover:bg-yellow-400 hover:text-sky-900 transition-colors"
                  data-testid="instagram-link"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>
        </div>

        {cities.length > 0 && (
          <div className="mt-12 pt-8 border-t border-white/10" data-testid="footer-liefergebiete">
            <h3 className="font-bold text-yellow-300 mb-4">Liefergebiete – Hüpfburg mieten in deiner Stadt</h3>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm text-sky-100/90">
              {cities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/standorte/${c.slug}`}
                  className="hover:text-yellow-300 transition-colors"
                  data-testid={`footer-city-${c.slug}`}
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-white/10 text-center text-sm text-sky-100/70 space-y-3">
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sky-100" data-testid="footer-legal-nav">
            <Link to="/impressum" className="hover:text-yellow-300 transition-colors" data-testid="footer-impressum">Impressum</Link>
            <span className="text-white/30">·</span>
            <Link to="/datenschutz" className="hover:text-yellow-300 transition-colors" data-testid="footer-datenschutz">Datenschutz</Link>
            <span className="text-white/30">·</span>
            <Link to="/agb" className="hover:text-yellow-300 transition-colors" data-testid="footer-agb">AGB</Link>
            <span className="text-white/30">·</span>
            <Link to="/widerruf" className="hover:text-yellow-300 transition-colors" data-testid="footer-widerruf">Widerrufsrecht</Link>
          </nav>
          <p>&copy; {new Date().getFullYear()} {settings?.brand?.site_name || 'Hüpfburgen Verleih'}. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
